/* eslint-disable max-lines */
import {
  useAdminTenantCameras,
  useToggleMaskDeadzone,
  useToggleVehicleDetection,
} from '@hakimo-ui/hakimo/data-access';
import { CameraPreviewModal } from '@hakimo-ui/hakimo/feature-cameras';
import { Camera } from '@hakimo-ui/hakimo/types';
import { BasicPanel } from '@hakimo-ui/hakimo/ui-elements';
import { Pagination, Table } from '@hakimo-ui/hakimo/ui-table';
import { InputField } from '@hakimo-ui/shared/ui-base';
import {
  ChevronDoubleDownIcon,
  ChevronDoubleUpIcon,
} from '@heroicons/react/24/solid';
import { useEffect, useMemo, useState } from 'react';
import { ToggleConfirmationModal } from '../components/ToggleConfirmationModal';
import { getCameraTableData } from '../util';

interface Props {
  tenantId: string;
  sourceSystem: string;
}

export function CameraTablesCard(props: Props) {
  const { tenantId, sourceSystem } = props;
  const [isExpanded, setIsExpanded] = useState(false);
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const pageSize = 10;

  const [previewCamera, setPreviewCamera] = useState<
    | (Camera & {
        clientCameraId?: string;
        processVehicle?: boolean;
        livestreamUrl?: string;
      })
    | null
  >(null);

  const {
    data: camerasData,
    isLoading,
    isFetching,
  } = useAdminTenantCameras(
    tenantId,
    true, // includeImage
    isExpanded // Fetching only when expanded
  );

  const [loadingCameraId, setLoadingCameraId] = useState<string | null>(null);
  const [loadingMaskedDeadzoneCameraId, setLoadingMaskedDeadzoneCameraId] =
    useState<string | null>(null);
  const [currentMaskDeadzoneCameraId, setCurrentMaskDeadzoneCameraId] =
    useState<string>('');

  // Confirmation modal states
  const [vehicleDetectionModal, setVehicleDetectionModal] = useState<{
    open: boolean;
    cameraId: string;
    currentValue: boolean;
  }>({ open: false, cameraId: '', currentValue: false });

  const [maskedDeadzoneModal, setMaskedDeadzoneModal] = useState<{
    open: boolean;
    cameraId: string;
    currentValue: boolean;
  }>({ open: false, cameraId: '', currentValue: false });

  const { mutate: toggleVehicleDetection, isLoading: isToggling } =
    useToggleVehicleDetection();

  const { mutate: toggleMaskDeadzone, isLoading: isMaskDeadzoneToggling } =
    useToggleMaskDeadzone(
      currentMaskDeadzoneCameraId,
      () => {
        setLoadingMaskedDeadzoneCameraId(null);
        setCurrentMaskDeadzoneCameraId('');
      },
      () => {
        setLoadingMaskedDeadzoneCameraId(null);
        setCurrentMaskDeadzoneCameraId('');
      }
    );

  // We determine if we are currently loading based on if loadingCameraId exists
  // and cameras is fetching. However since we changed the hook variables,
  // we can just rely on the cameras query refetching to clear the loading spinner.
  useEffect(() => {
    if (!isToggling && !isFetching && !isLoading) {
      setLoadingCameraId(null);
    }
  }, [isToggling, isFetching, isLoading]);

  useEffect(() => {
    if (!isMaskDeadzoneToggling && !isFetching && !isLoading) {
      setLoadingMaskedDeadzoneCameraId(null);
    }
  }, [isMaskDeadzoneToggling, isFetching, isLoading]);

  const handleToggleClick = () => {
    setIsExpanded((prev) => !prev);
    if (!isExpanded) {
      setPage(1); // Reset page when expanding
    } else {
      setSearchValue('');
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setPage(1);
  };

  const handleToggleVehicleDetection = (
    cameraId: string,
    currentValue: boolean
  ) => {
    setVehicleDetectionModal({
      open: true,
      cameraId,
      currentValue,
    });
  };

  const handleConfirmVehicleDetection = () => {
    setLoadingCameraId(vehicleDetectionModal.cameraId);
    toggleVehicleDetection({
      cameraId: vehicleDetectionModal.cameraId,
      isVehicleProcessingEnabled: !vehicleDetectionModal.currentValue,
      tenantId,
    });
    setVehicleDetectionModal({
      open: false,
      cameraId: '',
      currentValue: false,
    });
  };

  const handleToggleMaskedDeadzone = (
    cameraId: string,
    currentValue: boolean
  ) => {
    setMaskedDeadzoneModal({
      open: true,
      cameraId,
      currentValue,
    });
  };

  const handleConfirmMaskedDeadzone = () => {
    setLoadingMaskedDeadzoneCameraId(maskedDeadzoneModal.cameraId);
    setCurrentMaskDeadzoneCameraId(maskedDeadzoneModal.cameraId);
    setTimeout(() => {
      toggleMaskDeadzone({
        isMaskDeadzoneEnabled: !maskedDeadzoneModal.currentValue,
        tenantId,
      });
    }, 0);
    setMaskedDeadzoneModal({ open: false, cameraId: '', currentValue: false });
  };

  const filteredItems = useMemo(() => {
    if (!camerasData || !camerasData.items) {
      return [];
    }
    const query = searchValue.trim().toLowerCase();
    if (!query) {
      return camerasData.items;
    }
    return camerasData.items.filter((camera) =>
      camera.name?.toLowerCase().includes(query)
    );
  }, [camerasData, searchValue]);

  const tableData = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedItems = filteredItems.slice(startIndex, endIndex);

    return getCameraTableData(
      paginatedItems,
      handleToggleVehicleDetection,
      setPreviewCamera,
      loadingCameraId,
      handleToggleMaskedDeadzone,
      loadingMaskedDeadzoneCameraId
    );
  }, [
    filteredItems,
    page,
    pageSize,
    loadingCameraId,
    loadingMaskedDeadzoneCameraId,
    sourceSystem,
  ]);

  return (
    <>
      <BasicPanel
        title="Cameras"
        secondary={
          isExpanded && (
            <div className="w-64">
              <InputField
                label=""
                value={searchValue}
                onChange={handleSearchChange}
                type="search"
                placeholder="Search cameras by name..."
              />
            </div>
          )
        }
      >
        {isExpanded ? (
          <>
            {isLoading ? (
              <div className="p-4 text-center">Loading cameras...</div>
            ) : (
              <>
                <Table data={tableData} embedded />

                {filteredItems.length > 0 && (
                  <div className="border-t border-gray-200 p-4 dark:border-gray-800 [&>nav>p]:hidden">
                    <Pagination
                      from={(page - 1) * pageSize + 1}
                      to={Math.min(page * pageSize, filteredItems.length)}
                      pageSize={pageSize}
                      total={filteredItems.length}
                      page={page}
                      onChangePage={setPage}
                    />
                  </div>
                )}
              </>
            )}

            <div
              className="text-onlight-text-1 dark:text-ondark-text-1 hover:bg-hakimo-light-bg-hover dark:hover:bg-hakimo-dark-bg-hover flex cursor-pointer items-center justify-center gap-2 border-t border-gray-200 p-3 transition-colors dark:border-gray-800"
              onClick={handleToggleClick}
            >
              <ChevronDoubleUpIcon className="h-5 w-5" />
              <span className="text-sm font-semibold">Hide cameras</span>
            </div>
          </>
        ) : (
          <div
            className="text-onlight-text-1 dark:text-ondark-text-1 hover:bg-hakimo-light-bg-hover dark:hover:bg-hakimo-dark-bg-hover flex cursor-pointer items-center justify-center gap-2 p-4 transition-colors"
            onClick={handleToggleClick}
          >
            <ChevronDoubleDownIcon className="h-5 w-5" />
            <span className="text-sm font-semibold">Show cameras</span>
          </div>
        )}
      </BasicPanel>

      {previewCamera && (
        <CameraPreviewModal
          id={previewCamera.id}
          name={previewCamera.name}
          imageUrl={previewCamera.imageUrl}
          livestreamUrl={previewCamera.livestreamUrl}
          open={!!previewCamera}
          onClose={() => setPreviewCamera(null)}
        />
      )}

      <ToggleConfirmationModal
        open={vehicleDetectionModal.open}
        title={
          vehicleDetectionModal.currentValue
            ? 'Disable Vehicle Detection'
            : 'Enable Vehicle Detection'
        }
        message={
          <>
            <span className="text-onlight-text-2 dark:text-ondark-text-2 mb-2 block text-xs">
              {vehicleDetectionModal.currentValue
                ? `Vehicle detection will not run on this camera's feed.`
                : `Vehicle detection will run on this camera's feed.`}
            </span>
            {`Do you want to ${
              vehicleDetectionModal.currentValue ? 'disable' : 'enable'
            } Vehicle Detection?`}
          </>
        }
        onConfirm={handleConfirmVehicleDetection}
        onCancel={() =>
          setVehicleDetectionModal({
            open: false,
            cameraId: '',
            currentValue: false,
          })
        }
        isLoading={isToggling}
      />

      <ToggleConfirmationModal
        open={maskedDeadzoneModal.open}
        title={
          maskedDeadzoneModal.currentValue
            ? 'Disable Masked Deadzone'
            : 'Enable Masked Deadzone'
        }
        message={
          <>
            <span className="text-onlight-text-2 dark:text-ondark-text-2 mb-2 block text-xs">
              {maskedDeadzoneModal.currentValue
                ? `Deadzone areas will not be blacked out before frames are sent to the AI Operator.`
                : `Deadzone areas will be blacked out before frames are sent to the AI Operator.`}
            </span>
            {`Do you want to ${
              maskedDeadzoneModal.currentValue ? 'disable' : 'enable'
            } Masked Deadzone?`}
          </>
        }
        onConfirm={handleConfirmMaskedDeadzone}
        onCancel={() =>
          setMaskedDeadzoneModal({
            open: false,
            cameraId: '',
            currentValue: false,
          })
        }
        isLoading={isMaskDeadzoneToggling}
      />
    </>
  );
}

export default CameraTablesCard;
